import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface PollingOptions {
  interval?: number
  stopAfterFailedAttempts?: number
  onCleanUp?: () => Promise<void>
  autoStart?: boolean
  // When false, the first poll fires after `interval` instead of immediately on start. Useful when
  // another mount-time fetch already loaded the data and the leading tick would just duplicate it.
  leading?: boolean
}

export interface PollingControls {
  started: boolean
  start: () => void
  stop: () => void
}

export const usePollingEffect = (
  asyncCallback: () => Promise<void>,
  dependencies: unknown[] = [],
  { interval = 10_000, stopAfterFailedAttempts = 3, onCleanUp = undefined, autoStart = false, leading = true }: PollingOptions = {},
): PollingControls => {
  const [started, setStarted] = useState(autoStart)

  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const callbackRef = useRef(asyncCallback)

  useEffect(() => {
    callbackRef.current = asyncCallback
  }, [asyncCallback])

  useEffect(() => {
    if (!started) {
      return
    }

    let _stopped = false
    let failedAttempts = 0
    const pollingCallback = async () => {
      try {
        await callbackRef.current()
      } catch (e) {
        console.error(e)
        failedAttempts = failedAttempts + 1
        if (failedAttempts >= stopAfterFailedAttempts) {
          _stopped = true
          setStarted(false)
        }
      } finally {
        if (!_stopped) {
          timeoutIdRef.current = setTimeout(pollingCallback, interval)
        }
      }
    }

    if (leading) {
      pollingCallback().catch(console.error)
    } else {
      timeoutIdRef.current = setTimeout(pollingCallback, interval)
    }

    return () => {
      _stopped = true
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
      }

      if (onCleanUp) {
        onCleanUp().catch(console.error)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `dependencies` is a caller-supplied spread; the rest are the hook's own controls.
  }, [...dependencies, interval, started, onCleanUp, stopAfterFailedAttempts, leading])

  const start = useCallback(() => setStarted(true), [])
  const stop = useCallback(() => setStarted(false), [])

  return useMemo(() => {
    return {
      started,
      start,
      stop,
    } as PollingControls
  }, [started, start, stop])
}
